import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  PlayCircle,
  Crown,
  X,
  BookOpen,
  Brain,
  Code2,
  Target,
  Mic,
  Calendar,
  CheckCircle,
  Sparkles,
  Play,
  Pause,
  SkipForward,
  SkipBack,
  Volume2,
  Maximize,
  Clock3,
  Layers3,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { auth, db, functions } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { RiForward10Fill, RiSendBackward } from "react-icons/ri";
import { TbRewindBackward10, TbRewindForward10 } from "react-icons/tb";

const askAIMentor = httpsCallable(functions, "askAIMentor");
/* ================= LAYOUT ================= */
const isMVP = true;
const Page = styled.div`
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;

  padding: 120px 20px 80px;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);

  @media (max-width: 600px) {
    padding: 96px 14px 60px;
  }
`;

const Main = styled.div`
  width: 100%;
  max-width: 900px;
  min-width: 0; /* 🔥 VERY IMPORTANT */
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  font-weight: 900;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

/* ================= HERO ================= */

const Hero = styled.div`
  margin-top: 24px;
  padding: 40px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.12), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.36);

  @media (max-width: 600px) {
    padding: 22px 18px;
    border-radius: 22px;
  }
`;

const DayLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 900;
  color: #7dd3fc;
  text-transform: uppercase;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
`;

const Title = styled.h1`
  margin-top: 12px;
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 1000;
  line-height: 1.08;
  color: #f8fafc;
`;

const Text = styled.p`
  margin-top: 12px;
  font-size: 16px;
  line-height: 1.7;
  color: #94a3b8;
`;

const HeroMeta = styled.div`
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const HeroMetaPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.14);
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 800;
`;

const SummaryGrid = styled.div`
  margin-top: 22px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  padding: 18px 18px 16px;
  border-radius: 22px;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.78),
    rgba(2, 6, 23, 0.74)
  );
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.22);
`;

const SummaryEyebrow = styled.div`
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7dd3fc;
`;

const SummaryValue = styled.div`
  margin-top: 8px;
  font-size: 20px;
  font-weight: 900;
  color: #f8fafc;
`;

const SummaryHint = styled.p`
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.6;
  color: #94a3b8;
`;

/* ================= SECTIONS ================= */

const Section = styled.section`
  margin-top: 44px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
`;

const IconBox = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: ${({ color }) => color};
  color: white;
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 900;
  color: #f59e0b;
`;

const SectionBody = styled.div`
  padding-left: 48px;
  border-left: 2px dashed rgba(148, 163, 184, 0.25);

  @media (max-width: 600px) {
    padding-left: 0;
    border-left: none;
  }
`;

/* ================= CONTENT ================= */

const PillGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 100%;
`;

const Pill = styled.span`
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #cbd5e1;
`;

const List = styled.ul`
  margin-top: 10px;
  display: grid;
  gap: 10px;
  padding-left: 18px;

  li {
    line-height: 1.6;
    color: #dbeafe;
  }
`;

const ResourceList = styled.div`
  margin-top: 8px;
  display: grid;
  gap: 12px;
`;

const ResourceItem = styled.a`
  display: block;
  padding: 16px 18px;
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.14);
  color: #e2e8f0;
  text-decoration: none;
  transition:
    transform 0.18s ease,
    border-color 0.18s ease,
    background 0.18s ease;

  strong {
    display: block;
    font-size: 15px;
    color: #f8fafc;
  }

  span {
    display: block;
    margin-top: 6px;
    font-size: 13px;
    color: #94a3b8;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(56, 189, 248, 0.28);
    background: rgba(15, 23, 42, 0.88);
  }
`;

const CodeBlock = styled.pre`
  margin-top: 14px;
  padding: 16px;
  border-radius: 16px;

  background: #020617;
  border: 1px solid #1e293b;
  color: #e5e7eb;

  font-size: 14px;
  max-width: 100%;

  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

/* ================= PREMIUM ================= */

const PremiumCard = styled.div`
  margin-top: 98px;
  padding: 24px;

  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  max-height: 700px;
  border-radius: 28px;
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.14), transparent 34%);
  border: 1px solid rgba(245, 158, 11, 0.26);
  box-shadow: 0 20px 54px rgba(0, 0, 0, 0.3);

  @media (max-width: 600px) {
    padding: 18px 16px;
  }
`;

const PremiumBtn = styled.button`
  margin-top: 16px;
  padding: 12px;
  border-radius: 999px;
  border: none;

  font-weight: 900;
  cursor: pointer;
  background: linear-gradient(135deg, #f59e0b, #f97316);
  color: #020617;
  width: 100%;
  max-width: 100%;
  padding: 12px;
  border-radius: 999px;
`;
const EditorButton = styled.button`
  margin-top: 16px;
  width: 100%;
  padding: 12px;
  border-radius: 999px;
  border: none;
  font-weight: 900;
  cursor: pointer;

  background: linear-gradient(135deg, rgb(11, 93, 245), rgb(22, 124, 249));
  color: rgb(255, 255, 255);
`;
const ObjectBlock = styled.div`
  margin-top: 14px;
  padding-left: 18px;
  //  border-left: 3px solid rgba(59, 130, 246, 0.4);
`;

const ObjectHeading = styled.h4`
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 900;
  color: #60a5fa; /* blue highlight */
  text-transform: capitalize;
`;

const ObjectText = styled.p`
  font-size: 15px;
  line-height: 1.7;
  opacity: 0.9;
  margin-bottom: 6px;
`;

/* ================= MODAL ================= */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.7);
  display: grid;
  place-items: center;
  z-index: 5000;
`;

const Modal = styled.div`
  width: 90%;
  max-width: 420px;
  background: #020617;
  border-radius: 22px;
  padding: 24px;
  color: #e5e7eb;
`;

const PremiumModal = styled.div`
  width: 92%;
  max-width: 460px;
  background: linear-gradient(
    180deg,
    rgba(2, 6, 23, 0.98),
    rgba(2, 6, 23, 0.92)
  );
  border-radius: 26px;
  padding: 26px;
  color: #e5e7eb;
  border: 1px solid rgba(245, 158, 11, 0.4);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.65);
`;

const PremiumHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PremiumBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
  background: linear-gradient(135deg, #f59e0b, #f97316);
  color: #020617;
`;

const CloseIcon = styled.div`
  cursor: pointer;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;

const PremiumTitle = styled.h2`
  margin-top: 18px;
  font-size: 24px;
  font-weight: 1000;

  span {
    color: #f59e0b;
  }
`;

const PremiumSubtitle = styled.p`
  margin-top: 10px;
  font-size: 15px;
  line-height: 1.6;
  opacity: 0.9;

  strong {
    color: #fbbf24;
  }
`;

const FeatureList = styled.ul`
  margin-top: 20px;
  display: grid;
  gap: 12px;
  font-size: 14px;

  li {
    line-height: 1.5;
  }
`;

const ComingSoon = styled.div`
  margin-top: 22px;
  padding-top: 14px;
  border-top: 1px dashed rgba(148, 163, 184, 0.25);
  font-size: 13px;
  opacity: 0.85;
  color: #fbbf24;
`;

const PremiumCTA = styled.button`
  margin-top: 18px;
  width: 100%;
  padding: 14px;
  border-radius: 999px;
  border: none;
  font-size: 15px;
  font-weight: 900;
  cursor: not-allowed;
  background: linear-gradient(135deg, #f59e0b, #f97316);
  color: #020617;
  opacity: 0.6;
`;
const TreeWrapper = styled.div`
  position: relative;
  margin-left: 8px;
`;

const TreeNode = styled.div`
  position: relative;
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
`;

const TreeLine = styled.div`
  width: 2px;
  background: linear-gradient(
    to bottom,
    rgba(59, 130, 246, 0.6),
    rgba(59, 130, 246, 0.1)
  );
  margin-top: 4px;
`;

const TreeContent = styled.div`
  padding-left: 8px;
  flex: 1;
`;

const TreeTitle = styled.h4`
  font-size: 16px;
  font-weight: 900;
  color: #60a5fa;
  margin-bottom: 6px;
  text-transform: capitalize;
`;

const TreeText = styled.p`
  font-size: 15px;
  line-height: 1.7;
  opacity: 0.9;
  margin-bottom: 8px;
`;

const TreeList = styled.ul`
  margin-top: 6px;
  display: grid;
  gap: 6px;
  font-size: 14px;

  li {
    line-height: 1.6;
    opacity: 0.9;
  }
`;
const Layout = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: auto;

  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 32px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 28px;
  }
`;

const ContentBlock = styled.div`
  margin-top: 24px;
  padding: 24px 24px;
  border-radius: 24px;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.9),
    rgba(15, 23, 42, 0.7)
  );
  border: 1px solid rgba(148, 163, 184, 0.15);
  box-shadow: 0 18px 46px rgba(0, 0, 0, 0.24);
`;

const BlockTitle = styled.h3`
  font-size: 20px;
  font-weight: 900;
  margin-bottom: 10px;
  color: #60a5fa;
`;

const BlockText = styled.p`
  font-size: 15px;
  line-height: 1.7;
  opacity: 0.9;
`;

const BlockSubTitle = styled.h4`
  margin-top: 16px;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #fbbf24;
`;

const BlockList = styled.ul`
  margin-top: 6px;
  padding-left: 18px;
  display: grid;
  gap: 6px;

  li {
    line-height: 1.6;
    font-size: 14px;
  }
`;

const EmptyState = styled.div`
  margin-top: 24px;
  padding: 28px 24px;
  border-radius: 24px;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.82),
    rgba(2, 6, 23, 0.76)
  );
  border: 1px solid rgba(148, 163, 184, 0.14);
  color: #cbd5e1;
`;

const SkeletonLine = styled.div`
  background: linear-gradient(
    90deg,
    rgba(30, 41, 59, 0.86) 0%,
    rgba(51, 65, 85, 0.96) 50%,
    rgba(30, 41, 59, 0.86) 100%
  );
  background-size: 200% 100%;
  border-radius: 12px;
  animation: roadmapShimmer 1.5s linear infinite;

  @keyframes roadmapShimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const AIModal = styled.div`
  width: 92%;
  max-width: 720px;
  max-height: 85vh;

  background: linear-gradient(
    180deg,
    rgba(2, 6, 23, 0.98),
    rgba(2, 6, 23, 0.94)
  );

  border-radius: 26px;
  padding: 26px;
  color: #e5e7eb;

  border: 1px solid rgba(99, 102, 241, 0.25);
  box-shadow: 0 40px 120px rgba(0, 0, 0, 0.7);

  display: flex;
  flex-direction: column;
`;

const AIResponseBox = styled.div`
  margin-top: 18px;
  padding: 20px;
  border-radius: 18px;

  background: rgba(15, 23, 42, 0.85);
  border: 1px solid rgba(148, 163, 184, 0.2);

  font-size: 15px;
  line-height: 1.75;
  white-space: pre-wrap;

  max-height: 42vh;
  overflow-y: auto;

  scrollbar-width: thin;
  scrollbar-color: #6366f1 transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #6366f1, #4f46e5);
    border-radius: 999px;
  }
`;

const CopyBtn = styled.button`
  margin-top: 14px;
  width: 100%;
  padding: 10px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  font-weight: 900;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #020617;
`;
const VideoWrapper = styled.div`
  margin-top: 28px;
  width: 100%;
  border-radius: 22px;
  overflow: hidden;
  background: #020617;
  border: 1px solid rgba(148, 163, 184, 0.25);
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
`;

const Video = styled.video`
  width: 100%;
  max-height: 480px;
  background: black;
`;

const OverlayControls = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  pointer-events: none;
`;

const TapZone = styled.div`
  flex: 1;
`;

const CenterControl = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  pointer-events: none;
`;

const Controls = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  padding: 12px;
  background: linear-gradient(
    to top,
    rgba(2, 6, 23, 0.85),
    rgba(2, 6, 23, 0.1)
  );
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ControlBtn = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  display: grid;
  place-items: center;
`;

const SeekBar = styled.input`
  flex: 1;
`;

const TimeText = styled.span`
  font-size: 12px;
  opacity: 0.85;
  min-width: 80px;
  text-align: center;
`;

const Volume = styled.input`
  width: 80px;
`;
const OverlayRow = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  pointer-events: auto;
`;

const OverlayBtn = styled.button`
  background: rgba(2, 6, 23, 0.6);
  border: none;
  border-radius: 999px;
  padding: 16px;
  cursor: pointer;
  color: white;

  display: grid;
  place-items: center;

  backdrop-filter: blur(6px);

  &:hover {
    background: rgba(2, 6, 23, 0.8);
  }
`;

/* ================= HELPERS ================= */

const renderObject = (obj, level = 0) =>
  Object.entries(obj).map(([key, value], i) => {
    if (!value) return null;

    const title = key.replace(/([A-Z])/g, " $1");

    /* ---------- STRING ---------- */
    if (typeof value === "string") {
      return (
        <ObjectBlock key={i} style={{ marginLeft: level * 12 }}>
          <ObjectHeading>{title}</ObjectHeading>
          <ObjectText>{value}</ObjectText>
        </ObjectBlock>
      );
    }

    /* ---------- ARRAY ---------- */
    if (Array.isArray(value)) {
      return (
        <ObjectBlock key={i} style={{ marginLeft: level * 12 }}>
          <ObjectHeading>{title}</ObjectHeading>
          <List>
            {value.map((item, idx) =>
              typeof item === "string" ? (
                <li key={idx}>{item}</li>
              ) : (
                <li key={idx}>{renderObject(item, level + 1)}</li>
              )
            )}
          </List>
        </ObjectBlock>
      );
    }

    /* ---------- OBJECT ---------- */
    if (typeof value === "object") {
      return (
        <ObjectBlock key={i} style={{ marginLeft: level * 12 }}>
          <ObjectHeading>{title}</ObjectHeading>
          {renderObject(value, level + 1)}
        </ObjectBlock>
      );
    }

    return null;
  });

/* ================= COMPONENT ================= */
const DUMMY_VIDEO_URL = "https://www.w3schools.com/html/mov_bbb.mp4";
export default function RoadmapDayDetail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [showPremium, setShowPremium] = useState(false);
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiError, setAiError] = useState("");
  const todayKey = new Date().toISOString().split("T")[0];
  const [currentUser, setCurrentUser] = useState(null);
  const videoRef = React.useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [dayData, setDayData] = useState(null);
  const dayId = params.get("dayId");

  useEffect(() => {
    if (!dayId) return;

    const fetchDay = async () => {
      try {
        setIsLoading(true);
        const ref = doc(db, "roadmap_days", dayId);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setDayData(null);
        } else {
          setDayData({ id: snap.id, ...snap.data() });
        }
      } catch (err) {
        console.error("Failed to load day:", err);
        setDayData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDay();
  }, [dayId]);

  useEffect(() => {
    if (!user) return;

    const userRef = doc(db, "users", user.uid);
    return onSnapshot(userRef, (snap) => {
      if (snap.exists()) {
        setCurrentUser({ uid: user.uid, email: user.email, ...snap.data() });
      }
    });
  }, [user]);

  useEffect(() => {
    if (showAIModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showAIModal]);

  if (!dayId) {
    return <Page>Invalid day</Page>;
  }
  const ContentTree = ({ data, level = 0 }) => {
    if (!data || typeof data !== "object") return null;

    return (
      <TreeWrapper>
        {Object.entries(data).map(([key, value], idx) => {
          if (!value) return null;

          const title = key.replace(/([A-Z])/g, " $1");

          return (
            <TreeNode key={idx}>
              <TreeLine />

              <TreeContent>
                <TreeTitle>{title}</TreeTitle>

                {/* STRING */}
                {typeof value === "string" && <TreeText>{value}</TreeText>}

                {/* ARRAY */}
                {Array.isArray(value) && (
                  <TreeList>
                    {value.map((item, i) =>
                      typeof item === "string" ? (
                        <li key={i}>{item}</li>
                      ) : (
                        <li key={i}>
                          <ContentTree data={item} level={level + 1} />
                        </li>
                      )
                    )}
                  </TreeList>
                )}

                {/* OBJECT */}
                {typeof value === "object" && !Array.isArray(value) && (
                  <ContentTree data={value} level={level + 1} />
                )}
              </TreeContent>
            </TreeNode>
          );
        })}
      </TreeWrapper>
    );
  };
  const renderContent = (content) => {
    if (!content || typeof content !== "object") return null;

    return Object.entries(content).map(([key, value], index) => {
      if (value == null) return null;

      const label = key.replace(/([A-Z])/g, " $1");

      /* =====================
       TITLE → only VALUE
    ====================== */
      if (key === "title" && typeof value === "string") {
        return <BlockTitle key={index}>{value}</BlockTitle>;
      }

      /* =====================
       EXPLANATION → only VALUE
    ====================== */
      if (key === "explanation" && typeof value === "string") {
        return <BlockText key={index}>{value}</BlockText>;
      }

      /* =====================
       EXAMPLE(S)
    ====================== */
      if (key.toLowerCase().includes("example")) {
        return (
          <div key={index}>
            <BlockSubTitle>Example</BlockSubTitle>

            {Array.isArray(value) ? (
              value.map((ex, i) => <CodeBlock key={i}>{String(ex)}</CodeBlock>)
            ) : (
              <CodeBlock>{String(value)}</CodeBlock>
            )}
          </div>
        );
      }

      /* =====================
       REAL WORLD / USE CASES
    ====================== */
      if (
        key.toLowerCase().includes("use") ||
        key.toLowerCase().includes("real")
      ) {
        return (
          <div key={index}>
            <BlockSubTitle>Real World Usage</BlockSubTitle>
            <BlockList>
              {Array.isArray(value) ? (
                value.map((v, i) => <li key={i}>{String(v)}</li>)
              ) : (
                <li>{String(value)}</li>
              )}
            </BlockList>
          </div>
        );
      }

      /* =====================
       STRING
    ====================== */
      if (typeof value === "string") {
        return (
          <div key={index}>
            <BlockSubTitle>{label}</BlockSubTitle>
            <BlockText>{value}</BlockText>
          </div>
        );
      }

      /* =====================
       ARRAY
    ====================== */
      if (Array.isArray(value) && value.length > 0) {
        return (
          <div key={index}>
            <BlockSubTitle>{label}</BlockSubTitle>
            <BlockList>
              {value.map((item, i) =>
                typeof item === "object" ? (
                  <li key={i}>{renderContent(item)}</li>
                ) : (
                  <li key={i}>{String(item)}</li>
                )
              )}
            </BlockList>
          </div>
        );
      }

      /* =====================
       OBJECT (RECURSIVE)
    ====================== */
      if (typeof value === "object") {
        return (
          <div key={index}>
            <BlockSubTitle>{label}</BlockSubTitle>
            {renderContent(value)}
          </div>
        );
      }

      return null;
    });
  };

  const aiUsedToday = currentUser?.aiUsage?.lastUsedDate === todayKey;

  const canUseAI = currentUser?.plan === "PRO" && !aiUsedToday;
  const topicCount = dayData?.subTopics?.length || 0;
  const taskCount = dayData?.tasks?.length || 0;
  const resourceCount = dayData?.resources?.length || 0;
  const heroSummary = !dayData
    ? ""
    : dayData.explanation
    ? dayData.explanation
    : topicCount > 0
    ? `Today's lesson covers ${topicCount} focused topic${
        topicCount > 1 ? "s" : ""
      } so you can move forward with clarity instead of guessing your next step.`
    : "Focus on understanding the concept deeply, complete today's tasks, and turn this lesson into visible progress.";

  const handleAskAI = async () => {
    if (!canUseAI) return;

    try {
      setAiLoading(true);
      setAiError("");

      const res = await askAIMentor({
        today: {
          dayId: dayData.id,
          title: dayData.topic,
          subTopics: dayData.subTopics || [],
        },
      });

      // ✅ Callable functions return data inside `.data`
      setAiResult(res.data.message);
      setShowAIModal(true);
    } catch (err) {
      console.error("AI Mentor error:", err);

      setAiError(
        err.message ||
          "AI mentor is currently unavailable. Please try again later."
      );
    } finally {
      setAiLoading(false);
    }
  };

  const formatAIResponse = (text) => {
    return text
      .replace(/### (.*)/g, "🟡 **$1**")
      .replace(/Step \d+:/g, "🔹 **$&**")
      .replace(/- \*\*(.*?)\*\*/g, "✅ **$1**");
  };
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    video.paused ? video.play() : video.pause();
    setIsPlaying(!video.paused);
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;

    setCurrentTime(v.currentTime);
    setProgress((v.currentTime / v.duration) * 100);
  };

  const handleLoaded = () => {
    setDuration(videoRef.current.duration);
  };

  const handleSeek = (e) => {
    const v = videoRef.current;
    v.currentTime = (e.target.value / 100) * v.duration;
  };

  const skip = (sec) => {
    videoRef.current.currentTime += sec;
  };

  const handleVolume = (e) => {
    const v = Number(e.target.value);
    setVolume(v);
    videoRef.current.volume = v;
  };

  const formatTime = (t) =>
    `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;

  const fullscreen = () => {
    const el = videoRef.current;
    if (el.requestFullscreen) el.requestFullscreen();
  };
  return (
    <>
      <Page>
        <Layout>
          <Main>
            <BackBtn onClick={() => navigate(-1)}>
              <ArrowLeft size={18} /> Back
            </BackBtn>

            {isLoading ? (
              <>
                <Hero>
                  <DayLabel>
                    <Sparkles size={14} /> Loading lesson
                  </DayLabel>
                  <SkeletonLine
                    style={{
                      width: "min(420px, 100%)",
                      height: 42,
                      marginTop: 16,
                    }}
                  />
                  <SkeletonLine
                    style={{
                      width: "100%",
                      height: 16,
                      marginTop: 18,
                    }}
                  />
                  <SkeletonLine
                    style={{
                      width: "84%",
                      height: 16,
                      marginTop: 10,
                    }}
                  />
                  <HeroMeta>
                    <SkeletonLine
                      style={{ width: 96, height: 34, borderRadius: 999 }}
                    />
                    <SkeletonLine
                      style={{ width: 120, height: 34, borderRadius: 999 }}
                    />
                    <SkeletonLine
                      style={{ width: 104, height: 34, borderRadius: 999 }}
                    />
                  </HeroMeta>
                </Hero>

                <SummaryGrid>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <SummaryCard key={index}>
                      <SkeletonLine style={{ width: 92, height: 11 }} />
                      <SkeletonLine
                        style={{ width: "72%", height: 22, marginTop: 12 }}
                      />
                      <SkeletonLine
                        style={{ width: "100%", height: 14, marginTop: 14 }}
                      />
                      <SkeletonLine
                        style={{ width: "86%", height: 14, marginTop: 8 }}
                      />
                    </SummaryCard>
                  ))}
                </SummaryGrid>

                {Array.from({ length: 4 }).map((_, index) => (
                  <Section key={index}>
                    <SectionHeader>
                      <IconBox color="rgba(56, 189, 248, 0.14)">
                        <SkeletonLine
                          style={{ width: 16, height: 16, borderRadius: 6 }}
                        />
                      </IconBox>
                      <SkeletonLine style={{ width: 180, height: 18 }} />
                    </SectionHeader>
                    <SectionBody>
                      <ContentBlock>
                        <SkeletonLine style={{ width: "100%", height: 14 }} />
                        <SkeletonLine
                          style={{ width: "94%", height: 14, marginTop: 10 }}
                        />
                        <SkeletonLine
                          style={{ width: "88%", height: 14, marginTop: 10 }}
                        />
                        {index > 1 ? (
                          <SkeletonLine
                            style={{
                              width: "100%",
                              height: 42,
                              marginTop: 18,
                              borderRadius: 14,
                            }}
                          />
                        ) : null}
                      </ContentBlock>
                    </SectionBody>
                  </Section>
                ))}
              </>
            ) : !dayData ? (
              <EmptyState>
                <Title style={{ fontSize: "32px", marginTop: 0 }}>
                  This roadmap day is not available
                </Title>
                <Text style={{ marginTop: 10 }}>
                  The lesson may have been removed or the link is incomplete.
                  Go back to your roadmap and open the day again.
                </Text>
                <EditorButton
                  style={{ marginTop: 18, maxWidth: 240 }}
                  onClick={() => navigate("/my-roadmap")}
                >
                  Back to My Roadmap
                </EditorButton>
              </EmptyState>
            ) : (
              <>
                <Hero>
                  <DayLabel>
                    <Sparkles size={14} /> Today&apos;s Lesson
                  </DayLabel>
                  <Title>{dayData.topic}</Title>
                  <Text>{heroSummary}</Text>
                  <HeroMeta>
                    {dayData.day ? (
                      <HeroMetaPill>
                        <Calendar size={14} /> Day {dayData.day}
                      </HeroMetaPill>
                    ) : null}
                    {dayData.estimatedTime ? (
                      <HeroMetaPill>
                        <Clock3 size={14} /> {dayData.estimatedTime}
                      </HeroMetaPill>
                    ) : null}
                    {topicCount > 0 ? (
                      <HeroMetaPill>
                        <Layers3 size={14} /> {topicCount} topic
                        {topicCount > 1 ? "s" : ""}
                      </HeroMetaPill>
                    ) : null}
                  </HeroMeta>
                </Hero>

                <SummaryGrid>
                  <SummaryCard>
                    <SummaryEyebrow>Today&apos;s Focus</SummaryEyebrow>
                    <SummaryValue>
                      {topicCount > 0
                        ? `${topicCount} concepts to understand`
                        : "Concept-first learning"}
                    </SummaryValue>
                    <SummaryHint>
                      Start with the core concept, then move into content and
                      apply what you learned through today&apos;s tasks.
                    </SummaryHint>
                  </SummaryCard>

                  <SummaryCard>
                    <SummaryEyebrow>Execution Plan</SummaryEyebrow>
                    <SummaryValue>
                      {taskCount > 0
                        ? `${taskCount} task${taskCount > 1 ? "s" : ""} to complete`
                        : "Review and internalize"}
                    </SummaryValue>
                    <SummaryHint>
                      This day is designed to help you finish one focused block
                      of work instead of jumping between random topics.
                    </SummaryHint>
                  </SummaryCard>

                  <SummaryCard>
                    <SummaryEyebrow>Support Material</SummaryEyebrow>
                    <SummaryValue>
                      {resourceCount > 0
                        ? `${resourceCount} resource${resourceCount > 1 ? "s" : ""} ready`
                        : "Use built-in lesson notes"}
                    </SummaryValue>
                    <SummaryHint>
                      Open the helpful resources only when needed so you stay
                      anchored to the roadmap, not lost in endless tabs.
                    </SummaryHint>
                  </SummaryCard>
                </SummaryGrid>
              {/* <VideoWrapper>
                <VideoContainer>
                  <Video
                    ref={videoRef}
                    src={dayData.video.url}
                    onTimeUpdate={handleTimeUpdate}
                    controlsList="nodownload"
                    onLoadedMetadata={handleLoaded}
                    onEnded={() => setIsPlaying(false)}
                    onClick={togglePlay}
                  />

                  <OverlayControls>
                    <OverlayRow>
                      <OverlayBtn onClick={() => skip(-10)}>
                        <TbRewindBackward10 size={32} />
                      </OverlayBtn>

                      <OverlayBtn onClick={togglePlay}>
                        {isPlaying ? <Pause size={40} /> : <Play size={40} />}
                      </OverlayBtn>

                      <OverlayBtn onClick={() => skip(10)}>
                        <TbRewindForward10 size={32} />
                      </OverlayBtn>
                    </OverlayRow>
                  </OverlayControls>

          
                  <Controls>
                    <TimeText>
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </TimeText>

                    <SeekBar
                      type="range"
                      min="0"
                      max="100"
                      value={progress}
                      onChange={handleSeek}
                    />

                    <Volume
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={volume}
                      onChange={handleVolume}
                    />

                    <ControlBtn onClick={fullscreen}>
                      <Maximize size={18} />
                    </ControlBtn>
                  </Controls>
                </VideoContainer>
              </VideoWrapper> */}

              {/* ================= AI CTA ================= */}
              <Section>
                <SectionHeader>
                  <IconBox color="#22c55e">
                    <Brain size={18} />
                  </IconBox>
                  <SectionTitle>AI Mentor</SectionTitle>
                </SectionHeader>

                <SectionBody>
                  <ContentBlock>
                    <Text style={{ marginTop: 0 }}>
                      Stuck on this lesson, unsure what matters most, or not
                      able to connect the dots? Ask your mentor for a simpler
                      explanation and next best step for today.
                    </Text>

                    <EditorButton
                      disabled={!canUseAI || aiLoading}
                      onClick={handleAskAI}
                      style={{
                        opacity: !canUseAI ? 0.6 : 1,
                        cursor: !canUseAI ? "not-allowed" : "pointer",
                      }}
                    >
                      {aiLoading
                        ? "Thinking..."
                        : aiUsedToday
                        ? "AI used today · Come back tomorrow"
                        : "Ask AI Mentor for Today"}
                    </EditorButton>

                    {aiError && (
                      <Text style={{ color: "#f87171", marginTop: 8 }}>
                        {aiError}
                      </Text>
                    )}
                  </ContentBlock>
                </SectionBody>
              </Section>

              {dayData.subTopics?.length > 0 && (
                <Section>
                  <SectionHeader>
                    <IconBox color="#6366f1">
                      <BookOpen size={18} />
                    </IconBox>
                    <SectionTitle>Topics Covered</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                    <PillGroup>
                      {dayData.subTopics.map((t, i) => (
                        <Pill key={i}>{t}</Pill>
                      ))}
                    </PillGroup>
                  </SectionBody>
                </Section>
              )}

              {dayData.explanation && (
                <Section>
                  <SectionHeader>
                    <IconBox color="#3b82f6">
                      <Brain size={18} />
                    </IconBox>
                    <SectionTitle>Core Concept</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                    <ContentBlock>
                      <Text style={{ marginTop: 0 }}>{dayData.explanation}</Text>
                    </ContentBlock>
                  </SectionBody>
                </Section>
              )}

              {dayData.content && (
                <Section>
                  <SectionHeader>
                    <IconBox color="#0ea5e9">
                      <BookOpen size={18} />
                    </IconBox>
                    <SectionTitle>Learning Content</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                    <ContentBlock>
                      {renderContent(dayData.content)}
                    </ContentBlock>
                  </SectionBody>
                </Section>
              )}

              {dayData.tasks?.length > 0 && (
                <Section>
                  <SectionHeader>
                    <IconBox color="#22c55e">
                      <Target size={18} />
                    </IconBox>
                    <SectionTitle>Tasks for Today</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                    <ContentBlock>
                      <List>
                        {dayData.tasks.map((task, i) => (
                          <li key={i}>{task}</li>
                        ))}
                      </List>
                    </ContentBlock>
                  </SectionBody>
                </Section>
              )}

              {/* {dayData.useCases && (
                <Section>
                  <SectionHeader>
                    <IconBox color="#0ea5e9">
                      <BookOpen size={18} />
                    </IconBox>
                    <SectionTitle>Real-world Use Cases</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                    <List>
                      {dayData.useCases.map((u, i) => (
                        <li key={i}>{u}</li>
                      ))}
                    </List>
                  </SectionBody>
                </Section>
              )}

              {dayData.examples && (
                <Section>
                  <SectionHeader>
                    <IconBox color="#22c55e">
                      <Code2 size={18} />
                    </IconBox>
                    <SectionTitle>Worked Examples</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                    {dayData.examples.map((ex, i) => (
                      <CodeBlock key={i}>{ex}</CodeBlock>
                    ))}
                  </SectionBody>
                </Section>
              )}
              {dayData.cheatsheet && (
                <Section>
                  <SectionHeader>
                    <IconBox color="#3b82f6">
                      <BookOpen size={18} />
                    </IconBox>
                    <SectionTitle>Quick Cheatsheet</SectionTitle>
                  </SectionHeader>
                  <SectionBody>{renderObject(dayData.cheatsheet)}</SectionBody>
                </Section>
              )}

              {dayData.interviewInsights && (
                <Section>
                  <SectionHeader>
                    <IconBox color="#f59e0b">
                      <Mic size={18} />
                    </IconBox>
                    <SectionTitle>Interview Questions</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                    {renderObject(dayData.interviewInsights)}
                  </SectionBody>
                </Section>
              )} */}
              {dayData.estimatedTime && (
                <Section>
                  <SectionHeader>
                    <IconBox color="#6366f1">
                      <Brain size={18} />
                    </IconBox>
                    <SectionTitle>Estimated Time</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                    <ContentBlock>
                      <Text style={{ marginTop: 0 }}>
                        Reserve <strong>{dayData.estimatedTime}</strong> for
                        this lesson so you can complete it in one focused
                        sitting.
                      </Text>
                    </ContentBlock>
                  </SectionBody>
                </Section>
              )}

              {dayData.objectives?.length > 0 && (
                <Section>
                  <SectionHeader>
                    <IconBox color="#ec4899">
                      <Target size={18} />
                    </IconBox>
                    <SectionTitle>Learning Objectives</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                    <ContentBlock>
                      <List>
                        {dayData.objectives.map((o, i) => (
                          <li key={i}>{o}</li>
                        ))}
                      </List>
                    </ContentBlock>
                  </SectionBody>
                </Section>
              )}
              {dayData.resources?.length > 0 && (
                <Section>
                  <SectionHeader>
                    <IconBox color="#f59e0b">
                      <BookOpen size={18} />
                    </IconBox>
                    <SectionTitle>Helpful Resources</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                    <ContentBlock>
                      <ResourceList>
                        {dayData.resources.map((r, i) => (
                          <ResourceItem
                            key={i}
                            href={r.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <strong>{r.title || "Helpful resource"}</strong>
                            <span>
                              Open this only when you need extra support for
                              today&apos;s lesson.
                            </span>
                          </ResourceItem>
                        ))}
                      </ResourceList>
                    </ContentBlock>
                  </SectionBody>
                </Section>
              )}

              {dayData.editorType && (
                <EditorButton
                  style={{ marginTop: 60 }}
                  onClick={() => navigate(`/roadmap/day?dayId=${dayData.id}`)}
                >
                  <PlayCircle size={18} /> Practice & Apply
                </EditorButton>
              )}
              </>
            )}
          </Main>
          {dayData && currentUser?.plan != "PRO" && (
            <PremiumCard>
              <SectionHeader>
                <IconBox color="#22c55e">
                  <Sparkles size={18} />
                </IconBox>
                <SectionTitle>Consistency System</SectionTitle>
              </SectionHeader>

              <Text>
                Your roadmap is complete, but <strong>execution breaks most
                people</strong>. This system helps you stay consistent till the
                finish line.
              </Text>

              <FeatureList>
                <li>
                  <CheckCircle size={16} />
                  <div>
                    <strong>Daily Accountability</strong>
                    <span> Streaks, reminders & habit reinforcement</span>
                  </div>
                </li>

                <li>
                  <Calendar size={16} />
                  <div>
                    <strong>Smart Scheduling</strong>
                    <span> Calendar sync & missed-day recovery flow</span>
                  </div>
                </li>

                <li>
                  <Brain size={16} />
                  <div>
                    <strong>AI Learning Companion</strong>
                    <span> Explain concepts, unblock confusion, guide practice</span>
                  </div>
                </li>

                <li>
                  <Target size={16} />
                  <div>
                    <strong>Career Execution Signals</strong>
                    <span> When to revise, build projects & prepare interviews</span>
                  </div>
                </li>
              </FeatureList>

              <PremiumBtn onClick={() => setShowPremium(true)}>
                Unlock Consistency System
              </PremiumBtn>
            </PremiumCard>
          )}
        </Layout>

        {showPremium && (
          <Overlay onClick={() => setShowPremium(false)}>
            <PremiumModal onClick={(e) => e.stopPropagation()}>
              <PremiumHeader>
                <PremiumBadge>
                  <Crown size={16} /> Premium
                </PremiumBadge>
                <CloseIcon onClick={() => setShowPremium(false)}>
                  <X size={18} />
                </CloseIcon>
              </PremiumHeader>

              <PremiumTitle>
                Unlock Your <span>Full Potential</span>
              </PremiumTitle>

              <PremiumSubtitle>
                This lesson is just the beginning. Premium takes you to
                <strong> industry-level mastery.</strong>
              </PremiumSubtitle>

              <FeatureList>
                <li>🚀 Real-world projects & production patterns</li>
                <li>🎯 Advanced interview questions with answers</li>
                <li>🧠 AI mentor to explain concepts your way</li>
                <li>🧪 Testing, performance & best practices</li>
                <li>📈 Career & roadmap guidance</li>
              </FeatureList>

              <ComingSoon>
                ⏳ Premium access is launching soon.
                <br />
                Early users will get special benefits.
              </ComingSoon>

              <PremiumCTA disabled>
                🔒 Unlock Premium (Coming Soon)
              </PremiumCTA>
            </PremiumModal>
          </Overlay>
        )}
        {showAIModal && dayData && (
          <Overlay onClick={() => setShowAIModal(false)}>
            <AIModal onClick={(e) => e.stopPropagation()}>
              <PremiumHeader>
                <PremiumBadge>
                  <Brain size={16} /> AI Mentor
                </PremiumBadge>
                <CloseIcon onClick={() => setShowAIModal(false)}>
                  <X size={18} />
                </CloseIcon>
              </PremiumHeader>

              <PremiumTitle>
                Today&apos;s <span>Guidance</span>
              </PremiumTitle>

              <PremiumSubtitle>
                Focused explanation & clear next steps for
                <strong> Day {dayData.day}</strong>
              </PremiumSubtitle>

              <AIResponseBox>{formatAIResponse(aiResult)}</AIResponseBox>

              <CopyBtn
                onClick={() => {
                  navigator.clipboard.writeText(aiResult);
                }}
              >
                📋 Copy Response
              </CopyBtn>
            </AIModal>
          </Overlay>
        )}
      </Page>
    </>
  );
}
